import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Campaign {
  id: string
  name: string
  subject: string
  status: string
  audience_segment: string
  recipients_count: number
  sent_count: number
  delivered_count: number
  opened_count: number
  clicked_count: number
  open_rate: number
  click_rate: number
  scheduled_at: string | null
  sent_at: string | null
}

interface Template {
  id: string
  name: string
  category: string
  usage_count: number
}

interface Segment {
  id: string
  name: string
  count: number
  description: string
}

export default function EmailCampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [segments, setSegments] = useState<Segment[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [campRes, tplRes, segRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/email-campaigns/campaigns'),
      fetch(API_BASE + '/email-campaigns/templates'),
      fetch(API_BASE + '/email-campaigns/segments'),
      fetch(API_BASE + '/email-campaigns/statistics')
    ])
    if (campRes.ok) setCampaigns((await campRes.json()).campaigns || [])
    if (tplRes.ok) setTemplates((await tplRes.json()).templates || [])
    if (segRes.ok) setSegments((await segRes.json()).segments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800'
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'active': return 'bg-purple-100 text-purple-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Email Campaign Manager</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Campaign</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Campaigns</div>
              <div className="text-2xl font-bold">{statistics.total_campaigns}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Emails Sent</div>
              <div className="text-2xl font-bold text-blue-600">{(statistics.total_emails_sent / 1000).toFixed(0)}K</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Open Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.average_open_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Click Rate</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.average_click_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Templates</div>
              <div className="text-2xl font-bold">{statistics.total_templates}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Campaigns</h2></div>
            <div className="divide-y">
              {campaigns.map(campaign => (
                <div key={campaign.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.subject}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(campaign.status)}`}>{campaign.status}</span>
                  </div>
                  <div className="flex gap-6 text-sm mb-2">
                    <span><span className="text-gray-500">Recipients:</span> {campaign.recipients_count.toLocaleString()}</span>
                    <span><span className="text-gray-500">Sent:</span> {campaign.sent_count.toLocaleString()}</span>
                    <span className="text-green-600">{campaign.open_rate}% open</span>
                    <span className="text-blue-600">{campaign.click_rate}% click</span>
                  </div>
                  {campaign.status === 'sent' && (
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>Delivered: {campaign.delivered_count.toLocaleString()}</span>
                      <span>Opened: {campaign.opened_count.toLocaleString()}</span>
                      <span>Clicked: {campaign.clicked_count.toLocaleString()}</span>
                    </div>
                  )}
                  {campaign.scheduled_at && campaign.status === 'scheduled' && (
                    <div className="text-sm text-blue-600">Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Audience Segments</h3>
              <div className="space-y-3">
                {segments.map(segment => (
                  <div key={segment.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm">{segment.name}</span>
                      <span className="text-sm text-blue-600">{segment.count.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-gray-500">{segment.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Templates</h3>
              <div className="space-y-2">
                {templates.map(template => (
                  <div key={template.id} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-gray-500">{template.category}</div>
                    </div>
                    <span className="text-xs text-gray-500">{template.usage_count} uses</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
